import classNames from 'classnames';
import React from 'react';
import _ from 'underscore';

import ValidationInput from 'sharedreact/components/ValidationInput';

type ChangeValueData = {
  name: string,
  value: string
};

type Props = {
  className?: string,
  id: string,
  name: string,
  value?: string | number,
  disabled?: boolean,
  selected?: boolean,
  validations?: string[],
  type?: string,
  placeholder?: string,
  children?: React.ReactNode,
  validationTooltipPlacement?: string,
  validationTooltipClassName?: string,
  autoComplete?: string,
  inputRef?: (element: HTMLInputElement) => void,

  onLazyChangeValue?: (name: string, value: string) => void,
  onChangeValue?: (name: string, value: string, event?: React.ChangeEvent<HTMLInputElement>) => void,
  onEnter?: () => void,
  onBlur?: (name: string, value: string, event?: React.FocusEvent<HTMLInputElement>) => void,
};

type State = {
  intermediateValue: string | number;
};

type DefaultProps = Required<Pick<Props, 'value' | 'validations' | 'type' | 'placeholder'>>;

type PropsWithDefaults = Omit<Props, keyof DefaultProps> & DefaultProps;

class TextInput extends React.Component<Props, State> {
  static defaultProps: DefaultProps = {
    value: '',
    validations: [],
    type: 'text',
    placeholder: ''
  };

  private debouncedChangeValue: (() => void) | null
  private lastChangeValueData: ChangeValueData | null;

  constructor (props: Props) {
    super(props);

    this.state = {
      intermediateValue: props.value ?? ''
    }

    this.handleBlur = this.handleBlur.bind(this);
    this.handleChangeValue = this.handleChangeValue.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);

    this.handleDebouncedChangeValue = this.handleDebouncedChangeValue.bind(this);
    this.debouncedChangeValue = null;
    this.lastChangeValueData = null;

    if (props.onLazyChangeValue) {
      this.debouncedChangeValue = _.debounce(this.handleDebouncedChangeValue, 500);
    }
  }

  override componentDidMount (): void {
    const {
      id,
      selected
    } = this.props;

    if (selected) {
      const input: HTMLInputElement = document.getElementById(id) as HTMLInputElement;

      // The above flow type hack is needed to overcome not being able to
      // type-cast.

      let inputLength = 0;
      if (input.value) {
        inputLength = input.value.length;
      }

      if (input) {
        input.focus();
        input.setSelectionRange(0, inputLength);
      }
    }
  }
  
  override componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
    const value = this.props.value ?? '';
    const prevValue = prevProps.value ?? '';
    if (value !== prevValue && value != this.state.intermediateValue) {
      this.setState({intermediateValue: value});
    }
  }

  handleChangeValue (event: React.ChangeEvent<HTMLInputElement>): void {
    const {
      name,
      onChangeValue
    } = this.props;

    this.setState({intermediateValue: event.target.value});

    if (onChangeValue) {
      onChangeValue(name, event.target.value, event);
    }

    const debouncedChangeValue = this.debouncedChangeValue;
    if (debouncedChangeValue) {
      this.lastChangeValueData = {name: name, value: event.target.value || ''};
      debouncedChangeValue();
    }
  }

  handleDebouncedChangeValue () {
    const {
      name,
      onLazyChangeValue
    } = this.props;

    if (onLazyChangeValue) {
      const data = this.lastChangeValueData;

      if (data) {
        onLazyChangeValue(data.name, data.value);
        this.lastChangeValueData = null;
      }
    }
  }

  handleBlur (event: React.FocusEvent<HTMLInputElement>): void {
    const { name, onBlur } = this.props;

    if (onBlur) {
      onBlur(name, event.target.value, event);
    }
  }

  handleKeyDown (event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.nativeEvent.stopImmediatePropagation();
      event.currentTarget.blur();

      if (this.props.onEnter != null) {
        this.props.onEnter();
      }
    }
  }

  renderWithoutChildren () {
    const { className, id, name, disabled, validations, type,
      placeholder, validationTooltipPlacement, validationTooltipClassName,
      autoComplete, onBlur, onChangeValue, onLazyChangeValue, inputRef
    } = (this.props as PropsWithDefaults);

    return (
      <ValidationInput
        type={type}
        id={id}
        disabled={disabled}
        className={classNames({esko_disabledinput: disabled})}
        value={this.state.intermediateValue}
        placeholder={placeholder}
        onChange={onChangeValue != null || onLazyChangeValue != null ? this.handleChangeValue : undefined}
        onBlur={onBlur != null ? this.handleBlur : undefined}
        inputRef={inputRef}
        onKeyDown={this.handleKeyDown}
        name={name}
        validations={validations}
        validationTooltipPlacement={validationTooltipPlacement}
        validationTooltipClassName={validationTooltipClassName}
        containerClassName={className}
        autoComplete={autoComplete}
      />
    );
  }

  renderWithChildren () {
    const {
      className, id, name, value, disabled,
      validations, type, placeholder,
      children, validationTooltipPlacement,
      validationTooltipClassName, autoComplete,
      onBlur, onChangeValue
    } = (this.props as PropsWithDefaults);

    return (
      <div className={classNames('esko_input-group', className)}>
        <ValidationInput
          type={type}
          id={id}
          disabled={disabled}
          className={classNames({esko_disabledinput: disabled})}
          value={value ?? ''}
          placeholder={placeholder}
          onChange={onChangeValue != null ? this.handleChangeValue : undefined}
          onBlur={onBlur != null ? this.handleBlur : undefined}
          onKeyDown={this.handleKeyDown}
          name={name}
          validations={validations}
          validationTooltipPlacement={validationTooltipPlacement}
          validationTooltipClassName={validationTooltipClassName}
          autoComplete={autoComplete}
        />
        {children}
      </div>
    );
  }

  override render (): React.ReactNode {
    const { children } = this.props;

    if (children) {
      return this.renderWithChildren();
    } else {
      return this.renderWithoutChildren();
    }
  }
}

export default TextInput;
